<script setup>
import { defineAsyncComponent, onMounted, provide, ref, useTemplateRef } from 'vue'
import { useRoute } from 'vue-router'
import { formatLabel } from '@/libs/utils/translations.js'
import { useDropdown } from '@/composables/useDropdown.js'
import { buildDropdown, getBulkLabel, useBulkActions } from '@/composables/useBulkActions.js'
import { canDelegateRE, canDelegateREA } from '@/components/DefaultVr/general/utils.js'
import { formatDateLocale } from '@/libs/utils/date.js'
import { buildFilterQuery, formatImporto, pillColor, splitSuppliers } from '@/libs/utils.js'
import { getWorkflows, getStates } from '@/services/workflowService.js'
import { getCompiledPolls } from '@/services/compiledPollService.js'

import FiltriRicercaCompiledPoll from '@/components/CompiledPoll/Common/FiltriRicercaCompiledPoll.vue'
import Paginazione from '@/components/Generics/Paginations/Paginazione.vue'
import Loading from '@/components/Generics/Loadings/Loading.vue'

import TranslationProvider from '@/provider/translations.js'
import ConfigurationProvider from '@/provider/configuration.js'
import { useFiltersStore, usePageNumberStore } from '@/services/store.js'
import { toast } from '@/services/toast.js'
import { formatTransition, transitionsConfig } from '@/libs/utils/workflows.js'
import { client } from '@/services/fetch.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import {
  faCircleCheck,
  faCircleInfo,
  faCircleXmark,
  faEllipsisVertical,
  faEye,
  faGears,
  faKey,
  faPencil,
  faPenToSquare,
  faShare,
  faTrash
} from '@digitalpa/pro-solid-svg-icons'

library.add(
  faPenToSquare,
  faPencil,
  faEye,
  faEllipsisVertical,
  faKey,
  faShare,
  faTrash,
  faGears,
  faCircleCheck,
  faCircleXmark,
  faCircleInfo
)

const pageNumberStore = usePageNumberStore()
const filtersStore = useFiltersStore()
const route = useRoute()

const { campaignId = null } = defineProps(['campaignId'])
const compiledPolls = ref({
  data: [],
  pagination: {},
  columns: []
})
const isLoading = ref(true)
const currentPage = ref(1)
const networkError = ref({
  enabled: false,
  message: TranslationProvider.getTrans('app.network_error')
})

const modalConfirmChangeState = useTemplateRef('modal-confirm-change-state')
const currCompiledPoll = ref(null)
const currTransitionToApply = ref(null)

const tableList = useTemplateRef('tableList')

const modalConfirmDelete = useTemplateRef('modal-confirm-delete')
const currCompiledPollToDelete = ref(null)

const workflowList = ref([])
const statesList = ref([])
const transitionsList = ref({})
const modalReasonChangeState = useTemplateRef('modal-reason-change-state')
const reasonChangeState = ref(null)
const reasonChangeStateNote = ref(null)
const transitionConfig = ref(transitionsConfig)
const reasonChangeStateValidationError = ref(false)
const reasonChangeStateNoteValidationError = ref(false)
const loadingTransition = ref(false)
const currTransitionConfig = ref(null)
const currentFilters = ref('')
const campaignType = ref(ConfigurationProvider.getConfig().campaign['campaignType'])

const { dropDownToggleF, hideDropdowns } = useDropdown()
const {
  bulkActions,
  bulkActionsT,
  modalBulkActions,
  modalBulkActionsLoading,
  modalConfirmBulkActions,
  modalBulkActionsPayload,
  selectedBulkAction,
  selectedCps,
  bulkTransitions,
  bulkActionsDone,
  selectAll,
  toggleSelect,
  selectBulkAction,
} = useBulkActions(compiledPolls, transitionsList, [canDelegateREA, canDelegateRE])

onMounted(async () => {
  if (!route?.meta?.permissions?.canView) {
    return
  }

  currentPage.value = pageNumberStore.getPage(route.name) ?? 1
  await applicaFiltri()

  workflowList.value = await getWorkflows('compiled-poll')

  const { states, transitions } = await getStates(workflowList.value)
  statesList.value = states
  transitionsList.value = transitions

  showFiltersHandler(await filtersStore.getShowCard())
})

const applicaFiltri = async (page = 1) => {
  isLoading.value = true
  currentPage.value = page
  pageNumberStore.setPageNumber(page, route.name)

  selectedCps.value = []
  bulkTransitions.value = []
  bulkActionsDone.value = []

  const filtri = await filtersStore.filters
  currentFilters.value = buildFilterQuery(filtri)

  compiledPolls.value = await getCompiledPolls(page, currentFilters.value, campaignType['value'], campaignId)
  isLoading.value = false
}

const changePage = async page => {
  isLoading.value = true
  currentPage.value = page
  await applicaFiltri(page)
  isLoading.value = false
}
const applyTransition = async (
  compiledPoll,
  transition,
  apply = false,
  checkReason = false
) => {
  if (!apply) {
    modalConfirmChangeState.value.classList.add('d-block')
    currCompiledPoll.value = compiledPoll
    currTransitionToApply.value = transition
  } else {
    loadingTransition.value = true
    if (
      checkReason &&
      transitionConfig.value.some(tc =>
        currTransitionToApply.value['key'].endsWith(tc.key)
      )
    ) {
      transitionConfig.value.forEach(tc => {
        if (currTransitionToApply.value['key'].endsWith(tc.key)) {
          currTransitionConfig.value = tc
        }
      })

      modalReasonChangeState.value.classList.add('d-block')
      modalConfirmChangeState.value.classList.remove('d-block')
      loadingTransition.value = false
    } else {
      try {
        const cPoll = currCompiledPoll.value
        const cTransition = currTransitionToApply.value['key']
        let url = `/compiled-poll/api/compiled-poll/${cPoll}/workflow-transitions/${cTransition}`
        await client.post(url)
        await applicaFiltri(currentPage.value)

        const cTransitionLabel = formatTransition(
          currTransitionToApply.value['labels']['it']
        )
        toast.create((TranslationProvider.getTrans('app.state_changed')).replace('{transition}', cTransitionLabel).replace('{poll}', cPoll), 'success')
      } catch (error) {
        let message = TranslationProvider.getTrans('app.network_save_error')
        if (error.data && error.data.errors) {
          let required = 0
          let invalid = 0
          for (let e of error.data.errors) {
            if (e.params['dpa.error.type'] === 'required') {
              required++
            } else if (e.params['dpa.error.type'] === 'invalid') {
              invalid++
            } else if (e.params['dpa.error.type'] === 'form') {
              message += `<br> - ` + e.params['dpa.error.customMessage']
            }
          }
          if (required) {
            message += `<br> - ` + (TranslationProvider.getTrans('app.error_mandatory_answers')).replace('{required}', required)
          }
          if (invalid) {
            message += `<br> - ` + (TranslationProvider.getTrans('app.error_invalid_answers')).replace('{required}', required)
          }
        }
        toast.create(message, 'error')
      }
      currCompiledPoll.value = null
      currTransitionToApply.value = null
      loadingTransition.value = false
    }
    modalConfirmChangeState.value.classList.remove('d-block')
  }
}

const handleDeleteModal = async (compiledPoll, confirm = false) => {
  if (!confirm) {
    modalConfirmDelete.value.classList.add('d-block')
    currCompiledPollToDelete.value = compiledPoll
  } else {
    try {
      const cPoll = currCompiledPollToDelete.value
      let url = `/compiled-poll/api/compiled-poll/${cPoll}`
      await client.delete(url)
      await applicaFiltri(currentPage.value)
      toast.create((TranslationProvider.getTrans('app.delete_success')).replace('{cPoll}', cPoll))
    } catch (error) {
      toast.create(TranslationProvider.getTrans('app.delete_error'), 'error')
    }
    currCompiledPollToDelete.value = null
    modalConfirmDelete.value.classList.remove('d-block')
  }
}

const handleReasonModal = async () => {
  reasonChangeStateValidationError.value = false
  reasonChangeStateNoteValidationError.value = false
  if ((currTransitionConfig.value.reasons.length || currTransitionConfig.value.note_enabled) && (!reasonChangeState.value || !reasonChangeStateNote.value)) {
    if (currTransitionConfig.value.reasons.length && !reasonChangeState.value) {
      reasonChangeStateValidationError.value = true
    }
    if (currTransitionConfig.value.note_enabled && !reasonChangeStateNote.value) {
      reasonChangeStateNoteValidationError.value = true
    }
    return
  }
  loadingTransition.value = true
  const cPoll = currCompiledPoll.value
  const url = `/compiled-poll/api/compiled-poll/${cPoll}/add-or-update-attributes`
  try {
    let payload = {}
    payload['attributes'] = {}
    if (currTransitionConfig.value.reasons.length) {
      payload['attributes'][
        `polls.${currTransitionToApply.value['key']}.not_evaluated.reason`
        ] = reasonChangeState.value ?? ''
    }
    if (currTransitionConfig.value.note_enabled) {
      payload['attributes'][
        `polls.${currTransitionToApply.value['key']}.not_evaluated.reason.note`
        ] = reasonChangeStateNote.value ?? ''
    }
    if (currTransitionConfig.value.additional_attributes) {
      for (let attr of currTransitionConfig.value.additional_attributes) {
        payload['attributes'][
          `polls.${currTransitionToApply.value['key']}.${attr.key}`
          ] = attr.value.toString()
      }
    }

    if (currTransitionConfig.value['additional_inputs']) {
      for (const aInput of currTransitionConfig.value['additional_inputs']) {
        if (aInput.value) {
          payload['attributes'][
            `polls.${currTransitionToApply.value['key']}.${aInput['attribute_key']}`
            ] = aInput.value.toString()
        }
      }
    }

    const res = await client.put(url, payload)
    if (res && res.data && res.data.id) {
      await applyTransition(
        currCompiledPoll.value,
        currTransitionToApply.value,
        true,
        false
      )
    } else {
      toast.create('Errore nell\'applicazione della transizione, riprovare', error)
    }
  } catch (error) {
    toast.create('Errore nell\'applicazione della transizione, riprovare', error)
  }

  currCompiledPoll.value = null
  currTransitionToApply.value = null
  reasonChangeStateNote.value = null
  reasonChangeState.value = null
  modalReasonChangeState.value.classList.remove('d-block')
}

const showFiltersHandler = (showCard) => {
  if (!tableList.value)
    return
  if (showCard) {
    tableList.value.classList.add('list-f')
    tableList.value.classList.remove('list')
  } else {
    tableList.value.classList.add('list')
    tableList.value.classList.remove('list-f')
  }
}

const closeBulkActionsModal = async (reload = true) => {
  modalBulkActions.value.classList.remove('d-block')
  modalConfirmBulkActions.value.classList.remove('d-block')
  if (reload) {
    selectedCps.value = []
    bulkTransitions.value = []
    bulkActionsDone.value = []
    selectedBulkAction.value = null
    modalBulkActionsPayload.value = null
    await applicaFiltri(currentPage.value)
  }
}

provide('modalBulkActionsPayload', { modalBulkActionsPayload })
</script>

<template>
  <div class="px-0"
       @click="hideDropdowns"
  >
    <div class="card">
      <div class="card-body p-0">

        <filtri-ricerca-compiled-poll
          :isLoading="isLoading"
          :current-filters="currentFilters"
          :workflow-list="workflowList"
          :states-list="statesList"
          @applicaFiltri="applicaFiltri"
          @showCardChanged="showFiltersHandler"
        ></filtri-ricerca-compiled-poll>

        <loading v-if="isLoading"></loading>

        <div v-else class="table-responsive mt-1 list-f" ref="tableList">
          <table class="table table-striped mb-0">
            <thead>
            <tr>
              <th>
                <input
                  v-if="Object.keys(bulkActions).length" type="checkbox"
                  @change="selectAll"
                  :indeterminate="selectedCps.length > 0 && selectedCps.length < bulkActionsT.length"
                  :checked="selectedCps.length > 0 && selectedCps.length === bulkActionsT.length"
                />
              </th>
              <th class=" border-top-0"
                  :class="{
                      'text-end': item.type === 'date' || item.type === 'datetime'
                    }"
                  v-for="item in compiledPolls.columns"
                  :key="item"
                  v-html="TranslationProvider.getTrans(item.label)"
              >
              </th>
              <th v-if="!campaignId">Azioni</th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="!compiledPolls.data.length">
              <td :colspan="compiledPolls.columns.length + 2" class="text-center py-5">
                {{ TranslationProvider.getTrans('app.no_result_found') }}
              </td>
            </tr>
            <tr v-for="compiledPoll in compiledPolls.data" :key="compiledPoll.id">
              <td>
                <input
                  v-if="Object.keys(bulkActions).length"
                  type="checkbox"
                  ref="bulk-action"
                  :data-cpid="compiledPoll.id"
                  :data-state="compiledPoll.state"
                  @change="toggleSelect"
                />
              </td>
              <td v-for="item in compiledPolls.columns" :key="item.id"
                  :class="item['cssClass']"
              >
                  <span v-if="item.type === 'date' || item.type === 'datetime'">
                    {{ formatDateLocale(compiledPoll[item.field]) }}
                  </span>
                <span v-else-if="item.type === 'custom'">
                    <span v-if="item.field === 'pollSuppliers'">
                      <span
                        v-for="supplier in splitSuppliers(
                            compiledPoll['attributes']['gec.suppliers'],
                          )"
                        :key="supplier"
                      >
                        {{ supplier }}
                      </span>
                    </span>
                    <span v-else-if="item.field === 'stateTranslated'" class="badge rounded-pill"
                          :class="pillColor(compiledPoll)"
                    >
                        {{ compiledPoll['stateTranslated'] }}
                    </span>
                    <span v-else-if="item.field === 'importo'">
                      {{ formatImporto(compiledPoll['attributes']) }}
                    </span>
                  </span>
                <span v-else-if="item.type === 'attribute' && item.fields">
                      <template v-for="[field, formatLabelFunctionName] in Object.entries(item.fields)" :key="field">
                        <span v-if="compiledPoll['attributes'][field]"
                              v-html="formatLabel(formatLabelFunctionName, compiledPoll['attributes'][field])">
                        </span>
                        <br>
                      </template>
                  </span>
                <span v-else-if="item.type === 'attribute' && !item.fields && item.field">
                  {{ compiledPoll['attributes'][item.field] }}
                </span>
                <span v-else-if="item.field === 'campaignData.title'">
                  {{ compiledPoll['campaignData']?.title }}
                </span>
                <span v-else-if="item.field === 'campaignData'">
                  {{ TranslationProvider.getTrans(compiledPoll['campaignData'][item['subfield']]) }}
                </span>
                <span v-else>
                  {{ compiledPoll[item.field] }}
                </span>
              </td>
              <td v-if="!campaignId" class="text-end">
                <div class="d-flex justify-content-end">
                  <router-link
                    v-if="
                        compiledPoll['enabledActions'] &&
                        compiledPoll['enabledActions']['compile']
                      "
                    :to="{
                        name: 'admin/DefaultVr_CompiledPoll--edit',
                        params: { id: compiledPoll.id },
                        query: { page: currentPage}
                      }"
                  >
                    <button
                      class="btn btn-primary btn-sm ms-1"
                      :title="TranslationProvider.getTrans('app.compila')"
                    >
                      <font-awesome-icon icon="fa-pencil" />
                    </button>
                  </router-link>

                  <router-link
                    v-if="
                        compiledPoll['enabledActions'] &&
                        (compiledPoll['enabledActions']['settings'] || compiledPoll['enabledActions']['settings_show'])
                      "
                    :to="{
                        name: 'admin/DefaultVr_CompiledPollEditProperty',
                        params: { id: compiledPoll.id },
                        query: { page: currentPage }
                      }"
                  >
                    <button
                      class="btn btn-primary btn-sm ms-1"
                      :title="TranslationProvider.getTrans('app.settings')"
                    >
                      <font-awesome-icon icon="fa-gears" />
                    </button>
                  </router-link>

                  <router-link
                    v-if="
                        compiledPoll['enabledActions'] &&
                        compiledPoll['enabledActions']['read']
                      "
                    :to="{
                        name: 'admin/DefaultVr_CompiledPoll--detail',
                        params: { id: compiledPoll.id },
                        query: { page: currentPage }
                      }"
                  >
                    <button
                      class="btn btn-primary btn-sm ms-1"
                      :title="TranslationProvider.getTrans('app.show_compilazione')"
                    >
                      <font-awesome-icon icon=" fa-eye" />
                    </button>
                  </router-link>
                  <router-link
                    v-if="
                        compiledPoll['enabledActions'] &&
                        compiledPoll['enabledActions']['permissions']
                      "
                    :to="{
                        name: 'admin/DefaultVr_Permissions',
                        params: { id: compiledPoll.id, type: 'compiled-poll' }
                      }"
                  >
                    <button
                      class="btn btn-primary btn-sm ms-1"
                      :title="TranslationProvider.getTrans('app.assign_permissions')"
                    >
                      <font-awesome-icon icon=" fa-key" />
                    </button>
                  </router-link>
                  <div
                    class="dropdown ms-1"
                    v-if="compiledPoll['enabledTransitions'].length"
                  >
                    <button
                      class="btn btn-sm btn-primary dropdown-toggle-b"
                      :title="TranslationProvider.getTrans('app.change_state')"
                      @click="dropDownToggleF"
                      ref="dropdown-toggle"
                      type="button"
                      data-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <font-awesome-icon
                        icon="fa-share"
                        class="dropdown-toggle-icon"
                      />
                    </button>
                    <ul class="dropdown-menu dropdown-menu-right">
                      <li>
                        <a
                          class="dropdown-item"
                          href="#"
                          v-for="et in compiledPoll['enabledTransitions']"
                          :key="et.key"
                          @click="applyTransition(compiledPoll.id, et, false)"
                        >
                          {{ formatTransition(et['labels']['it']) }}
                        </a>
                      </li>
                    </ul>
                  </div>

                  <button
                    v-if="
                        compiledPoll['enabledActions'] &&
                        compiledPoll['enabledActions']['delete']
                      "
                    class="btn btn-danger btn-sm ms-1"
                    :title="TranslationProvider.getTrans('app.delete_compilazione')"
                    @click="handleDeleteModal(compiledPoll.id)"
                  >
                    <font-awesome-icon icon="fa-trash" />
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
          <div
            class="d-flex justify-content-between m-2 mt-4 align-items-end align-content-end"
          >
            <div>
              <div class="dropdown">
                <button
                  class="btn btn-secondary dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  v-if="Object.entries(bulkActions).length"
                  :disabled="!selectedCps.length"
                >
                  {{ TranslationProvider.getTrans('app.actions', 'Azioni') }}
                </button>
                <ul class="dropdown-menu">
                  <template v-for="(types, workflow) in buildDropdown(bulkActions)">
                    <li>
                      <h6 class="dropdown-header text-start text-black mb-0 mt-2 pb-0">{{ TranslationProvider.getTrans(workflow) }}</h6>
                    </li>
                    <li
                      v-for="(bulkActions,type) in types"
                    >
                      <button
                        v-for="(bulkAction) in bulkActions"
                        class="dropdown-item" type="button"
                        @click="selectBulkAction(type, bulkAction)"
                        v-html="TranslationProvider.getTrans(getBulkLabel(type, bulkAction))"
                      >
                      </button>
                    </li>
                  </template>
                </ul>
              </div>
            </div>
            <Paginazione
              v-if="
              Math.ceil(
                parseInt(compiledPolls.pagination['total_items']) /
                  parseInt(compiledPolls.data.length),
              ) > 1
            "
              :total-pages="
                Math.ceil(
                  compiledPolls.pagination['total_items'] /
                    compiledPolls.pagination['items_per_page'],
                )
              "
              @page-changed="changePage"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    class="modal"
    tabindex="-1"
    ref="modal-confirm-change-state"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Aggiornamento stato compilazione</h5>
          <button
            type="button"
            class="btn-close border-0 bg-white"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="
              () => {
                modalConfirmChangeState.classList.remove('d-block')
              }
            "
          ></button>
        </div>
        <div class="modal-body" v-if="currCompiledPoll">
          <p
            v-html="TranslationProvider.getTrans('app.compiled_poll.save_change_state', null, {'{state}': formatTransition(currTransitionToApply['labels']['it'], true) })"
          >
          </p>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="
              () => {
                modalConfirmChangeState.classList.remove('d-block')
              }
            "
          >
            Annulla
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="
              applyTransition(
                currCompiledPoll,
                currTransitionToApply,
                true,
                true,
              )
            "
            :disabled="loadingTransition"
          >
            <span v-if="!loadingTransition">
              {{ TranslationProvider.getTrans('app.procedi') }}
            </span>
            <span v-if="loadingTransition">
              <span
                class="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
              <span class="visually-hidden" role="status">Loading...</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="!campaignId"
    class="modal"
    tabindex="-1"
    ref="modal-confirm-delete"
    style="background-color: rgba(0, 0, 0, 0.8)"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ TranslationProvider.getTrans('app.modal_delete.title') }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="
              () => {
                modalConfirmDelete.classList.remove('d-block')
              }
            "
          ></button>
        </div>
        <div class="modal-body" v-if="currCompiledPollToDelete">
          <p
            v-html="(TranslationProvider.getTrans('app.modal_delete.body')).replace('{value}', currCompiledPollToDelete)"></p>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="
              () => {
                modalConfirmDelete.classList.remove('d-block')
              }
            "
          >
            Chiudi
          </button>
          <button
            type="button"
            class="btn btn-danger"
            @click="handleDeleteModal(currCompiledPoll, true)"
          >
            Elimina
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="!campaignId"
    class="modal"
    tabindex="-1"
    ref="modal-reason-change-state"
    style="background-color: rgba(0, 0, 0, 0.8)"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ TranslationProvider.getTrans('app.selezionare_una_motivazione') }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="
              () => {
                modalReasonChangeState.classList.remove('d-block')
                reasonChangeStateNote = null
                reasonChangeState = null
              }
            "
          ></button>
        </div>
        <div class="modal-body">
          <p>
            {{ TranslationProvider.getTrans('app.inserisci_la_motivazione_per_questo_cambio_di_stato') }}
          </p>
          <div v-if="currTransitionConfig && currTransitionConfig.reasons.length">
            <v-select
              :options="currTransitionConfig.reasons"
              v-model="reasonChangeState"
              placeholder="Seleziona una motivazione"
            >
              <template #no-options>
                <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
              </template>
            </v-select>
            <p class="mb-0 text-danger" v-if="reasonChangeStateValidationError">
              {{ TranslationProvider.getTrans('app.mandatory_field') }}
            </p>
          </div>
          <div class="mt-2" v-if="currTransitionConfig && currTransitionConfig.note_enabled">
            <textarea
              class="form-control"
              v-model="reasonChangeStateNote"
              placeholder="Note..."
            >
            </textarea>
            <p
              class="mb-0 text-danger"
              v-if="reasonChangeStateNoteValidationError"
            >
              {{ TranslationProvider.getTrans('app.mandatory_field') }}
            </p>
          </div>

          <template v-if="currTransitionConfig && currTransitionConfig['additional_inputs']">
            <div v-for="(aInput, index) in currTransitionConfig['additional_inputs']" :key="aInput['attribute_key']">
              <label>{{ aInput.label }}</label>
              <input
                :type="aInput.type"
                @input="(event)=>{currTransitionConfig['additional_inputs'][index].value = event.target.value}"
                :class="aInput.class"
              >
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="
              () => {
                modalReasonChangeState.classList.remove('d-block')
                reasonChangeStateNote = null
                reasonChangeState = null
              }
            "
          >
            Chiudi
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="handleReasonModal"
            :disabled="loadingTransition"
          >
            <span v-if="!loadingTransition">
              {{ TranslationProvider.getTrans('app.send') }}
            </span>
            <span v-if="loadingTransition">
              <span
                class="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
              <span class="visually-hidden" role="status">Loading...</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    class="modal"
    tabindex="-1"
    ref="modal-confirm-bulk-actions"
    style="background-color: rgba(0, 0, 0, 0.8)"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
         v-if="selectedBulkAction"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ TranslationProvider.getTrans('app.bulk_actions', 'Conferma azione massiva') }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="closeBulkActionsModal(false)"
          ></button>
        </div>
        <div class="modal-body">
          {{
            TranslationProvider.getTrans('app.confirm_bulk_action', 'Si vuole procedere con l\'azione massiva ' + getBulkLabel(selectedBulkAction.type, selectedBulkAction.action) + '?')
          }}
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="closeBulkActionsModal(false)"
          >
            {{ TranslationProvider.getTrans('app.annulla') }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            data-bs-dismiss="modal"
            @click="selectBulkAction(selectedBulkAction.type, selectedBulkAction.action, true)"
          >
            {{ TranslationProvider.getTrans('app.conferma') }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    class="modal"
    tabindex="-1"
    ref="modal-bulk-actions"
    style="background-color: rgba(0, 0, 0, 0.8)"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ TranslationProvider.getTrans('app.bulk_actions', 'Riepilogo azione massiva') }}
          </h5>
          <button
            v-if="!modalBulkActionsLoading"
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="closeBulkActionsModal"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="modalBulkActionsLoading" class="d-flex justify-content-center">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <table class="table table-sm">
            <thead>
            <tr>
              <th scope="col">
                {{ TranslationProvider.getTrans('app.questionario', 'Questionario') }}
              </th>
              <th scope="col">
                {{ TranslationProvider.getTrans('app.risultato', 'Risultato') }}
              </th>
            </tr>
            </thead>
            <tbody>
            <template
              v-for="(obj) in bulkActionsDone"
            >
              <tr>
                <td :class="obj.done ? '' : 'border-bottom-0'">
                  {{ obj.compiledPoll['attributes']['gec.contract_object'] }}
                </td>
                <td :class="obj.done ? '' : 'border-bottom-0'">
                  <font-awesome-icon icon="fa-circle-check" class="text-success" v-if="obj.done" />
                  <div v-else>
                    <font-awesome-icon
                      icon="fa-circle-info" class="text-primary me-1"
                      type="button" data-bs-toggle="collapse"
                      :data-bs-target="'#collapse'+obj.compiledPoll['id']" aria-expanded="false"
                      :aria-controls="'collapse'+obj.compiledPoll['id']"
                    />
                    <font-awesome-icon icon="fa-circle-xmark" class="text-danger" />
                  </div>
                </td>
              </tr>
              <tr v-if="!obj.done">
                <td colspan="2">
                  <div class="collapse" :id="'collapse'+obj.compiledPoll['id']">
                    <p class="fw-bold mb-0">Errori</p>
                    <div v-html="obj.error" />
                  </div>
                </td>
              </tr>
            </template>
            </tbody>
          </table>
        </div>
        <div class="modal-footer">
          <button
            v-if="!modalBulkActionsLoading"
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="closeBulkActionsModal"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    class="modal"
    tabindex="-1"
    ref="modal-confirm-bulk-actions"
    style="background-color: rgba(0, 0, 0, 0.8)"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered"
         v-if="selectedBulkAction !== null"
    >
      <component
        v-if="selectedBulkAction.customModalContent"
        :selected-bulk-action="selectedBulkAction"
        @close-modal="closeBulkActionsModal(false)"
        @confirm="selectBulkAction(selectedBulkAction.type, selectedBulkAction.action, true)"
        :is="defineAsyncComponent(() => import(`./bulkActions/${selectedBulkAction.customModalContent}.vue`))"
      >
      </component>
      <div class="modal-content" v-else>
        <div class="modal-header">
          <h5 class="modal-title" v-if="selectedBulkAction.title">
            {{ TranslationProvider.getTrans(selectedBulkAction.title) }}
          </h5>
          <h5 class="modal-title" v-else>
            {{ TranslationProvider.getTrans('app.bulk_actions', 'Conferma azione massiva') }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="closeBulkActionsModal(false)"
          ></button>
        </div>
        <div
          class="modal-body"
          v-html="TranslationProvider.getTrans('app.confirm_bulk_action', 'Si vuole procedere con l\'azione massiva ' + getBulkLabel(selectedBulkAction.type, selectedBulkAction.action) + '?')"
        >
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="closeBulkActionsModal(false)"
          >
            {{ TranslationProvider.getTrans('app.annulla') }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            data-bs-dismiss="modal"
            @click="selectBulkAction(selectedBulkAction.type, selectedBulkAction.action, true)"
          >
            {{ TranslationProvider.getTrans('app.conferma') }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    class="modal"
    tabindex="-1"
    ref="modal-bulk-actions"
    style="background-color: rgba(0, 0, 0, 0.8)"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ TranslationProvider.getTrans('app.bulk_actions', 'Riepilogo azione massiva') }}
          </h5>
          <button
            v-if="!modalBulkActionsLoading"
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="closeBulkActionsModal"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="modalBulkActionsLoading" class="d-flex justify-content-center">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <table class="table table-sm">
            <thead>
            <tr>
              <th scope="col">
                {{ TranslationProvider.getTrans('app.questionario', 'Questionario') }}
              </th>
              <th scope="col">
                {{ TranslationProvider.getTrans('app.risultato', 'Risultato') }}
              </th>
            </tr>
            </thead>
            <tbody>
            <template
              v-for="(obj) in bulkActionsDone"
            >
              <tr>
                <td :class="obj.done ? '' : 'border-bottom-0'">
                  {{ obj.compiledPoll['attributes']['gec.contract_object'] }}
                </td>
                <td :class="obj.done ? '' : 'border-bottom-0'">
                  <font-awesome-icon icon="fa-circle-check" class="text-success" v-if="obj.done" />
                  <div v-else>
                    <font-awesome-icon
                      icon="fa-circle-info" class="text-primary me-1"
                      type="button" data-bs-toggle="collapse"
                      :data-bs-target="'#collapse'+obj.compiledPoll['id']" aria-expanded="false"
                      :aria-controls="'collapse'+obj.compiledPoll['id']"
                    />
                    <font-awesome-icon icon="fa-circle-xmark" class="text-danger" />
                  </div>
                </td>
              </tr>
              <tr v-if="!obj.done">
                <td colspan="2">
                  <div class="collapse" :id="'collapse'+obj.compiledPoll['id']">
                    <p class="fw-bold mb-0">Errori</p>
                    <div v-html="obj.error" />
                  </div>
                </td>
              </tr>
            </template>
            </tbody>
          </table>
        </div>
        <div class="modal-footer">
          <button
            v-if="!modalBulkActionsLoading"
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="closeBulkActionsModal"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">

.container-fluid,
.modal {

  & .table-responsive {
    overflow-y: visible;
  }

  & .vs__search {
    font-size: 1rem;
  }

  & .vs__dropdown-toggle {
    font-size: 1rem;
  }

  & .vs__dropdown-toggle, .vs__dropdown-toggle {
    border-color: #dee2e6;
  }
}

:root,
:host {
  @extend :root;
}

[class*='fa-'] {
  width: 0.9rem;
}

.fa-ellipsis-vertical {
  width: 0.2rem;
}

.col-importo {
  min-width: 100px;
}

.pill-r span:first-of-type {
  background: #0d6efd;
}

.select-massive-delegati .vs__dropdown-toggle {
  padding: 10px 0 10px 0 !important;
}
</style>
